import { clamp01 } from '../../utils/math';
import type { CeilingZone, GroundSurface, SegmentTerrain, TrackArea } from '../segments/SegmentDefinition';

/**
 * Pure evaluation of a segment's terrain (segment-local space). The ground is a
 * height field: base ground everywhere, replaced by surfaces (platforms, ramps)
 * and removed by gaps (pits); surfaces win over gaps. Ceilings are solid from
 * their clearance upwards – slabs (ceilings with a `top`, the crown of a walk-in
 * pipe) only up to their top, which is walkable for a runner who gets up there.
 *
 * Slabs depend on where the runner is: `topReach` (its feet + one step) splits them.
 * Tops up to it are ground the runner stands on, higher slabs are ceilings / walls in
 * its way. Queries without a runner (static analysis, safe-ground search) pass nothing –
 * for them every slab is a ceiling (NO_TOP_REACH).
 *
 * Areas are half-open boxes: a point on the far edge belongs to the next zone.
 */
export const EMPTY_TERRAIN: SegmentTerrain = { surfaces: [], gaps: [], ceilings: [] };

/** topReach of queries that stand nobody on a slab: every slab counts as a ceiling. */
export const NO_TOP_REACH = -Infinity;

/** Tolerance of the top-reach comparison (feet + step are computed in floating point). */
const TOP_REACH_EPSILON = 1e-9;

/** Which ceilings an area query considers. */
export type CeilingFilter = 'all' | 'hard' | 'soft';

/** A walkable top at `top` lies within the runner's reach (slabs here, obstacle roofs in ObstacleManager). */
export function withinTopReach(top: number, topReach: number): boolean {
  return top <= topReach + TOP_REACH_EPSILON;
}

/** The slab's top lies within the runner's reach: ground under its feet, no ceiling. */
export function isUnderfoot(ceiling: CeilingZone, topReach: number): boolean {
  return ceiling.top !== undefined && withinTopReach(ceiling.top, topReach);
}

/** Height of a surface at z (linear between heightFrom and heightTo, clamped to the surface). */
export function surfaceHeightAt(surface: GroundSurface, z: number): number {
  const length = surface.zTo - surface.zFrom;
  const t = length > 0 ? clamp01((z - surface.zFrom) / length) : 0;
  return surface.heightFrom + (surface.heightTo - surface.heightFrom) * t;
}

export function containsPoint(area: TrackArea, z: number, x: number): boolean {
  return z >= area.zFrom && z < area.zTo && x >= area.xFrom && x < area.xTo;
}

export function overlapsArea(area: TrackArea, minZ: number, maxZ: number, minX: number, maxX: number): boolean {
  return maxZ > area.zFrom && minZ < area.zTo && maxX > area.xFrom && minX < area.xTo;
}

function enclosesArea(area: TrackArea, minZ: number, maxZ: number, minX: number, maxX: number): boolean {
  return minZ >= area.zFrom && maxZ <= area.zTo && minX >= area.xFrom && maxX <= area.xTo;
}

/** Walkable height at a point, or null over a pit. */
export function groundHeightAt(terrain: SegmentTerrain, base: number, z: number, x: number): number | null {
  let top = -Infinity;
  for (const surface of terrain.surfaces) {
    if (containsPoint(surface, z, x)) top = Math.max(top, surfaceHeightAt(surface, z));
  }
  if (top > -Infinity) return top;
  for (const gap of terrain.gaps) if (containsPoint(gap, z, x)) return null;
  return base;
}

/**
 * Highest solid ground anywhere inside the area, or null if the whole area lies over
 * a pit. Used for support (the runner stands on the highest ground under its footprint)
 * and for wall tests (anything higher than a step in the way is a wall).
 *
 * Base ground counts unless the area lies completely inside one surface or one gap –
 * conservative where several zones meet, which only matters below the base level
 * (inside a pit, where the rim really is a wall). Slab tops up to `topReach` count as
 * ground as well – higher ones never: they are ceilings for that runner, not walls.
 */
export function highestGroundIn(
  terrain: SegmentTerrain,
  base: number,
  minZ: number,
  maxZ: number,
  minX: number,
  maxX: number,
  topReach: number = NO_TOP_REACH,
): number | null {
  let top = -Infinity;
  let enclosed = false;
  for (const surface of terrain.surfaces) {
    if (!overlapsArea(surface, minZ, maxZ, minX, maxX)) continue;
    // Linear along z → the maximum lies on one end of the overlapping z range.
    top = Math.max(top, surfaceHeightAt(surface, Math.max(minZ, surface.zFrom)), surfaceHeightAt(surface, Math.min(maxZ, surface.zTo)));
    if (enclosesArea(surface, minZ, maxZ, minX, maxX)) enclosed = true;
  }
  if (!enclosed) {
    for (const gap of terrain.gaps) {
      if (enclosesArea(gap, minZ, maxZ, minX, maxX)) {
        enclosed = true;
        break;
      }
    }
  }
  if (!enclosed) top = Math.max(top, base);
  for (const ceiling of terrain.ceilings) {
    if (ceiling.top !== undefined && isUnderfoot(ceiling, topReach) && overlapsArea(ceiling, minZ, maxZ, minX, maxX)) top = Math.max(top, ceiling.top);
  }
  return top === -Infinity ? null : top;
}

function matches(ceiling: CeilingZone, filter: CeilingFilter): boolean {
  if (filter === 'all') return true;
  return filter === 'soft' ? ceiling.soft === true : ceiling.soft !== true;
}

/** Lowest ceiling underside (≥ minHeight) above the area, or null. Slabs within `topReach` are underfoot – no ceiling. */
export function lowestCeilingIn(
  terrain: SegmentTerrain,
  minZ: number,
  maxZ: number,
  minX: number,
  maxX: number,
  minHeight: number,
  filter: CeilingFilter = 'all',
  topReach: number = NO_TOP_REACH,
): number | null {
  let lowest: number | null = null;
  for (const ceiling of terrain.ceilings) {
    if (ceiling.clearance < minHeight || !matches(ceiling, filter) || isUnderfoot(ceiling, topReach)) continue;
    if (!overlapsArea(ceiling, minZ, maxZ, minX, maxX)) continue;
    lowest = lowest === null ? ceiling.clearance : Math.min(lowest, ceiling.clearance);
  }
  return lowest;
}

/** Floor of the pit at a point (base − depth), or null where there is no pit. */
export function pitFloorAt(terrain: SegmentTerrain, base: number, z: number, x: number): number | null {
  if (groundHeightAt(terrain, base, z, x) !== null) return null;
  let floor: number | null = null;
  for (const gap of terrain.gaps) {
    if (containsPoint(gap, z, x)) floor = floor === null ? base - gap.depth : Math.max(floor, base - gap.depth);
  }
  return floor;
}
